import React, { useState } from "react";
import classNames from "classnames";
import ModalShell from "./ModalShell";
import { CheckMark } from "../_shared/icons";
import type { BlurTarget } from "../_shared/exportPdf";
import buttons from "./GenerateReportModal.module.scss";
import styles from "./ExportOptionsModal.module.scss";

type ExportOption = "school" | "people" | "campus";

const svg = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const SchoolIcon = (
  <svg {...svg}>
    <path d="M3 21h18" />
    <path d="M5 21V9l7-5 7 5v12" />
    <path d="M10 21v-5h4v5" />
    <path d="M12 10.5h.01" />
  </svg>
);

const PeopleIcon = (
  <svg {...svg}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="17" cy="9" r="2.4" />
    <path d="M16.2 14.1A5 5 0 0 1 21 19" />
  </svg>
);

const CampusIcon = (
  <svg {...svg}>
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const EyeIcon = (
  <svg {...svg} width={15} height={15}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (
  <svg {...svg} width={15} height={15}>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4.1" />
    <path d="M6.6 6.6C3.7 8.5 2 12 2 12s3.6 7 10 7a9.6 9.6 0 0 0 4.4-1" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const DownloadIcon = (
  <svg {...svg} width={15} height={15} strokeWidth={2}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const OPTIONS: {
  key: ExportOption;
  label: string;
  hint: string;
  icon: React.ReactNode;
  /** the names this choice hides in the PDF */
  targets: BlurTarget[];
}[] = [
  {
    key: "school",
    label: "School name",
    hint: "The name in the report header",
    icon: SchoolIcon,
    targets: ["school"],
  },
  {
    key: "people",
    label: "Teacher & student names",
    hint: "In every list, ranking and table",
    icon: PeopleIcon,
    targets: ["teacher", "student"],
  },
  {
    key: "campus",
    label: "Campus names",
    hint: "In every campus row and comparison",
    icon: CampusIcon,
    targets: ["campus"],
  },
];

/** Checkbox square; `mixed` draws a dash for a partly ticked "Select all". */
const Box = ({ on, mixed = false }: { on: boolean; mixed?: boolean }) => (
  <span className={classNames(styles.box, (on || mixed) && styles.boxOn)}>
    {on ? <CheckMark /> : mixed ? <span className={styles.dash} /> : null}
  </span>
);

interface ExportOptionsModalProps {
  /** campus names only exist on a head office's dashboard */
  showCampus?: boolean;
  onClose: () => void;
  /** called with every name kind to blur — empty exports the full text */
  onExport: (blur: BlurTarget[]) => void;
}

/** Asked before every export: which names, if any, to blur in the PDF. */
export default function ExportOptionsModal({
  showCampus = false,
  onClose,
  onExport,
}: ExportOptionsModalProps) {
  const [picked, setPicked] = useState<ExportOption[]>([]);
  const options = OPTIONS.filter((o) => showCampus || o.key !== "campus");
  const count = options.filter((o) => picked.includes(o.key)).length;
  const allOn = count === options.length;

  const toggle = (key: ExportOption) =>
    setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const toggleAll = () => setPicked(allOn ? [] : options.map((o) => o.key));

  const footer = (
    <div className={styles.footer}>
      <span className={classNames(styles.status, count > 0 && styles.statusOn)}>
        {count > 0 ? EyeOffIcon : EyeIcon}
        {count > 0
          ? `${count} of ${options.length} will be blurred`
          : "Full text — nothing blurred"}
      </span>
      <div className={styles.actions}>
        <button type="button" className={buttons.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={classNames(buttons.generateBtn, styles.exportBtn)}
          onClick={() =>
            onExport(
              options
                .filter((o) => picked.includes(o.key))
                .flatMap((o) => o.targets),
            )
          }
        >
          {DownloadIcon}
          Export PDF
        </button>
      </div>
    </div>
  );

  return (
    <ModalShell
      title="Export report"
      subtitle="Hide names before you share the PDF."
      onClose={onClose}
      footer={footer}
      maxWidth={480}
    >
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>Blur in PDF</span>
        <button
          type="button"
          role="checkbox"
          aria-checked={allOn ? true : count > 0 ? "mixed" : false}
          className={styles.selectAll}
          onClick={toggleAll}
        >
          <Box on={allOn} mixed={count > 0} />
          Select all
        </button>
      </div>

      <div className={styles.list}>
        {options.map((o) => {
          const on = picked.includes(o.key);
          return (
            <button
              type="button"
              role="checkbox"
              aria-checked={on}
              key={o.key}
              className={classNames(styles.option, on && styles.optionOn)}
              onClick={() => toggle(o.key)}
            >
              <span className={styles.icon}>{o.icon}</span>
              <span className={styles.text}>
                <span className={styles.label}>{o.label}</span>
                <span className={styles.hint}>{o.hint}</span>
              </span>
              <Box on={on} />
            </button>
          );
        })}
      </div>
    </ModalShell>
  );
}
